import { useApp } from 'contexts/app';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Container,
    Icon,
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableRow,
    Stack,
    styled,
    Text,
    TextInput,
} from 'shell/components';
import { FEEDBACK_LIMIT } from 'utils/constants';
import { getUserPreferredFullName, last4 } from 'utils/helper';

const Tablet = ({ filters, setFilters, getFilterCount, onClickBack, onClickView, onClickViewAll }) => {
    const { t } = useTranslation();
    const { studentAccountUsers, currentAssignment, feedback: allFeedback } = useApp();

    const feedback = allFeedback.filter(({ owner_account_id }) => {
        return studentAccountUsers.some((student) => student.owner_account_id === owner_account_id);
    });

    return (
        <Container device={'tablet'}>
            <CustomStack sx={{ flex: 1 }} spacing={5}>
                <CustomStack alignItems={'start'} spacing={3}>
                    <Button startIcon={<Icon kind={'arrowLeft'} />} variant={'borderless'} onClick={onClickBack}>
                        {t('controls.back', 'Back')}
                    </Button>
                    <CustomStack direction={'row'} justifyContent={'space-between'}>
                        <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                            {currentAssignment.title}
                        </Text>
                        <Stack sx={{ flexShrink: 0 }} direction={'row'} spacing={4}>
                            <CustomButton
                                color={'neutral'}
                                startIcon={<Icon kind={'filter'} />}
                                variant={'secondary'}
                                onClick={() => setFilters((current) => ({ ...current, open: true }))}
                            >
                                {t('controls.filters', 'Filters')} ({getFilterCount(filters)})
                            </CustomButton>
                            <TextInput
                                endIcon={<Icon kind={'search'} />}
                                placeholder={t('controls.search', 'Search')}
                                value={filters.search}
                                onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                            />
                        </Stack>
                    </CustomStack>
                </CustomStack>
                <ScrollableTable sx={{ flex: 1 }} aria-label={'list of feedback'}>
                    <ScrollableTableHead>
                        <ScrollableTableRow>
                            <CustomScrollableTableCell>{t('table.user-id', 'User Id')}</CustomScrollableTableCell>
                            <CustomScrollableTableCell>
                                {t('table.student-name', 'Student Name')}
                            </CustomScrollableTableCell>
                            <CustomScrollableTableCell>
                                {t('table.last-generation', 'Last Generation')}
                            </CustomScrollableTableCell>
                            <CustomScrollableTableCell>
                                {t('table.attempts-used', 'Attempts Used')}
                            </CustomScrollableTableCell>
                            <CustomScrollableTableCell>
                                <Stack direction={'row'} justifyContent={'space-between'}>
                                    <Text color={'inherit'}>{t('ui.feedback', 'Feedback')}</Text>
                                    <Button disabled={!feedback.length} variant={'borderless'} onClick={onClickViewAll}>
                                        {t('controls.view-all', 'View All')}
                                    </Button>
                                </Stack>
                            </CustomScrollableTableCell>
                        </ScrollableTableRow>
                    </ScrollableTableHead>
                    <ScrollableTableBody>
                        {studentAccountUsers
                            .filter((accountUser) => {
                                return getUserPreferredFullName(accountUser)
                                    .toLowerCase()
                                    .includes(filters.search.toLowerCase());
                            })
                            .sort((a, b) => {
                                const aName = getUserPreferredFullName(a).toLowerCase();
                                const bName = getUserPreferredFullName(b).toLowerCase();
                                if (filters.sort === 'namea-z') {
                                    if (aName < bName) return -1;
                                    if (aName > bName) return 1;
                                }
                                if (filters.sort === 'namez-a') {
                                    if (aName < bName) return 1;
                                    if (aName > bName) return -1;
                                }
                                return 0;
                            })
                            .map((accountUser) => {
                                const feedbackItems = feedback.filter(
                                    ({ owner_account_id }) => owner_account_id === accountUser.owner_account_id
                                );
                                const lastCreatedOn = Math.max(...feedbackItems.map(({ created_on }) => created_on));
                                return (
                                    <ScrollableTableRow key={accountUser.owner_account_id}>
                                        <CustomScrollableTableCell>
                                            {last4(accountUser.user_id)}
                                        </CustomScrollableTableCell>
                                        <CustomScrollableTableCell>
                                            {getUserPreferredFullName(accountUser)}
                                        </CustomScrollableTableCell>
                                        <CustomScrollableTableCell>
                                            {feedbackItems.length
                                                ? t(
                                                      'ui.format-date-time-sign',
                                                      '{{val, date_short}} @ {{val, time_only}}',
                                                      {
                                                          val: lastCreatedOn,
                                                      }
                                                  )
                                                : '-'}
                                        </CustomScrollableTableCell>
                                        <CustomScrollableTableCell>
                                            {feedbackItems.length}/{FEEDBACK_LIMIT}
                                        </CustomScrollableTableCell>
                                        <CustomScrollableTableCell>
                                            <Button
                                                disabled={!feedbackItems.length}
                                                variant={'borderless'}
                                                onClick={() =>
                                                    onClickView(
                                                        feedbackItems.sort((a, b) => b.created_on - a.created_on)[0]
                                                    )
                                                }
                                            >
                                                {t('controls.view', 'View')}
                                            </Button>
                                        </CustomScrollableTableCell>
                                    </ScrollableTableRow>
                                );
                            })}
                    </ScrollableTableBody>
                </ScrollableTable>
            </CustomStack>
        </Container>
    );
};

const CustomStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomButton = styled(Button)(() => {
    return {
        flexShrink: 0,
        height: 'fit-content',
    };
});

const CustomScrollableTableCell = styled(ScrollableTableCell)(() => {
    return {
        width: '25%',
    };
});

export default Tablet;
